import React from "react";

interface SvgProps {
  className?: string;
  width?: number | string;
  height?: number | string;
}

/**
 * Filled warning triangle matching Domino's UK `ic-ui-notification-warning`
 * exactly — both paths lifted from their SVG sprite (viewBox 0 0 32 32):
 *  - triangle: amber fill via `currentColor` (set with a `text-*` class), with
 *    `strokeLinejoin="round"` to reproduce their rounded corners.
 *  - exclamation mark: their dark `#30282c`, independent of the triangle tint.
 */
export const WarningIcon: React.FC<SvgProps> = ({
  className,
  width = 24,
  height = 24,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label="warning"
  >
    <path
      d="M16 2.182l16 27.636H0L16 2.182z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinejoin="round"
    />
    <path
      d="M17.436 10.8v5.309c0 2.51-.291 4.836-.436 5.382h-2c-.145-.545-.436-2.872-.436-5.382V10.8h2.873zm.309 13.855a1.745 1.745 0 11-3.49 0 1.745 1.745 0 013.49 0z"
      fill="#30282c"
    />
  </svg>
);

export const InfoIcon: React.FC<SvgProps> = ({
  className,
  width = 18,
  height = 18,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M8.98321 0.369141C13.5727 0.369141 17.3277 4.12418 17.3277 8.71368C17.3277 13.3032 13.5727 17.0582 8.98321 17.0582C4.39371 17.0582 0.638672 13.3032 0.638672 8.71368C0.638672 4.20763 4.22682 0.536031 8.73287 0.369141H8.98321ZM9.98455 7.21166H8.14875V13.3032H9.98455V7.21166ZM9.06665 4.04074C8.39909 4.04074 7.89842 4.54141 7.89842 5.20897C7.89842 5.87653 8.39909 6.37721 9.06665 6.37721C9.73422 6.37721 10.2349 5.87653 10.2349 5.20897C10.1514 4.54141 9.65077 4.04074 9.06665 4.04074Z"
      fill="currentColor"
    />
  </svg>
);

export const StoreIcon: React.FC<SvgProps> = ({
  className,
  width = 29,
  height = 30,
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox="0 0 29 30"
    fill="none"
  >
    <path
      d="M23.9401 12.9756H4.70047C4.48402 12.9756 4.31568 13.144 4.31568 13.358V25.8024C4.31568 26.0165 4.48402 26.1836 4.70047 26.1836H23.9401C24.1565 26.1836 24.3248 26.0165 24.3248 25.8024V13.3568C24.3252 13.3063 24.3154 13.2563 24.2961 13.2097C24.2769 13.1631 24.2485 13.1208 24.2126 13.0853C24.1768 13.0497 24.1342 13.0218 24.0874 13.0029C24.0406 12.9841 23.9905 12.9748 23.9401 12.9756ZM23.6274 3.98833H5.97509C5.75865 3.98833 5.5903 4.15547 5.5903 4.36951V5.22808H24.0122V4.36951C24.0125 4.31906 24.0028 4.26905 23.9835 4.22242C23.9642 4.17579 23.9358 4.13347 23.9 4.09796C23.8642 4.06246 23.8216 4.03447 23.7748 4.01564C23.728 3.99681 23.6779 3.98753 23.6274 3.98833ZM16.1721 6.15759L17.1581 12.0461H11.7951L12.4925 6.15759H16.1721ZM20.7655 6.15759L22.1363 12.0461H18.1201L17.11 6.15759H20.7655ZM23.8439 6.15759L26.5133 12.0461H23.0983L21.7275 6.15759H23.8439ZM11.5546 6.15759L10.8571 12.0461H6.45608L7.80285 6.15759H11.5546ZM6.84087 6.15759L5.4941 12.0461H2.46387L5.13336 6.15759H6.84087Z"
      fill="white"
    />
  </svg>
);

export const BasketIcon: React.FC<SvgProps> = ({
  className,
  width = 25,
  height = 25,
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox="0 0 25 25"
    fill="none"
  >
    <path
      d="M24.1039 8.91371C23.7396 8.54944 23.2545 8.30684 22.8902 8.30684H21.3122L15.4868 1.02515C15.2442 0.660881 14.6374 0.539957 14.2731 0.782555C13.9088 1.02515 13.7879 1.63202 14.1514 1.99629V2.11797L19.1273 8.42851H5.53506L10.5109 2.11797C10.8752 1.7537 10.7535 1.2685 10.3893 0.904229C10.025 0.539957 9.5398 0.661632 9.17552 1.0259V1.14758L3.35017 8.42927H2.13643C1.16529 8.30759 0.4375 9.15781 0.4375 10.0073V10.3715L2.62238 21.2937C2.98665 23.2352 4.68559 24.6916 6.62712 24.6916H18.2778C20.2194 24.6916 21.919 23.2352 22.2826 21.2937L24.3458 10.3715C24.459 10.1331 24.4964 9.86568 24.453 9.60531C24.4096 9.34494 24.2875 9.1041 24.1032 8.91521L24.1039 8.91371Z"
      fill="currentColor"
    />
  </svg>
);

interface RibbonEndProps {
  colors: {
    main: string;
    dark: string;
    shadow: string;
  };
  side: "left" | "right";
  className?: string;
}

export const DominosLogoWhite: React.FC<SvgProps> = ({
  className,
  width = 235,
  height = 52,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    enableBackground="new 0 0 817 182"
    viewBox="0 0 817 182"
    width={width}
    height={height}
    className={className}
  >
    <g transform="translate(0 2.91)">
      <defs>
        <filter
          id="astrap"
          width="176.18"
          height="176.09"
          x="0"
          y=".74"
          filterUnits="userSpaceOnUse"
        >
          <feColorMatrix values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 1 0"></feColorMatrix>
        </filter>
        <style>{`:root{--neutral-white:rgba(245, 245, 245, 1);--primary-blue--brand-blue:rgba(0, 100, 145, 1);--primary-red-brand-red:rgba(227, 24, 55, 1)}`}</style>
      </defs>
      <mask
        id="bstrap"
        width="176.18"
        height="176.09"
        x="0"
        y=".74"
        maskUnits="userSpaceOnUse"
      >
        <g filter="url(#astrap)">
          <path
            fill="var(--neutral-white)"
            fillRule="evenodd"
            d="M0 .74h176.18v176.09H0z"
            clipRule="evenodd"
          ></path>
        </g>
      </mask>
      <path
        fill="var(--neutral-white)"
        fillRule="evenodd"
        d="M173.06 51.7L125.2 3.87a10.63 10.63 0 00-7.57-3.13c-2.85 0-5.54 1.1-7.56 3.13L69.94 43.98 3.13 110.74a10.7 10.7 0 000 15.12l47.86 47.84a10.63 10.63 0 007.56 3.13c2.86 0 5.54-1.11 7.56-3.13L126 113.85l47.06-47.04a10.7 10.7 0 000-15.11"
        clipRule="evenodd"
        mask="url(#bstrap)"
      ></path>
    </g>
    <path
      fill="var(--primary-red-brand-red)"
      fillRule="evenodd"
      d="M127.58 71.84a13.86 13.86 0 11-19.6-19.6 13.86 13.86 0 0119.6 19.6m42.1-13.83L121.8 10.18a5.9 5.9 0 00-8.35 0L61.7 61.9l56.21 56.18 51.75-51.72a5.9 5.9 0 000-8.35"
      clipRule="evenodd"
    ></path>
    <path
      fill="var(--primary-blue--brand-blue)"
      fillRule="evenodd"
      d="M69.04 111.78a13.8 13.8 0 019.8-4.06c3.54 0 7.1 1.35 9.8 4.06a13.77 13.77 0 010 19.59 13.9 13.9 0 01-19.6 0 13.81 13.81 0 010-19.59zm-21.08 19.04a13.92 13.92 0 01-19.6 0 13.85 13.85 0 1123.66-9.8c0 3.54-1.36 7.1-4.06 9.8zM6.51 117.04a5.89 5.89 0 000 8.35l47.86 47.84a5.92 5.92 0 008.36 0l51.72-51.7-56.21-56.18-51.73 51.7z"
      clipRule="evenodd"
    ></path>
    <path
      fill="white"
      fillRule="evenodd"
      d="M108.8 149.3a14 14 0 016.9 1.82 12.8 12.8 0 015.21 5.16 14.02 14.02 0 01.05 14.02 13.2 13.2 0 01-5.17 5.2 14.14 14.14 0 01-13.98 0 13.09 13.09 0 01-5.18-5.2 14.08 14.08 0 01-1.87-6.97c0-2.45.64-4.8 1.91-7.05 1.25-2.24 3-3.98 5.2-5.16a14 14 0 016.93-1.81zm.02 2.76c-1.93 0-3.8.47-5.55 1.44a10.34 10.34 0 00-4.19 4.15 11.36 11.36 0 004.13 15.47 11.32 11.32 0 0011.21 0 11.3 11.3 0 004.14-15.47 11.33 11.33 0 00-9.74-5.6zm-6.36 18.78v-14.9h3.02a203 203 0 014.76.05c1 .09 1.78.28 2.34.58a4.2 4.2 0 011.94 3.57c0 1.07-.36 1.98-1.06 2.76a4.76 4.76 0 01-2.96 1.4c.46.18.81.36 1.06.57.24.2.58.58 1 1.16.11.13.48.73 1.1 1.8l1.76 3h-3.75l-1.24-2.42c-.82-1.62-1.53-2.64-2.07-3.06a3.3 3.3 0 00-2.1-.63h-.78v6.12h-3.02zm3.02-8.46h1.22c1.75 0 2.83-.06 3.26-.21.42-.12.74-.38.98-.7.26-.35.37-.73.37-1.15 0-.41-.1-.78-.35-1.1a2 2 0 00-1.02-.71c-.44-.13-1.5-.2-3.24-.2h-1.22v4.07zm133.45-11.65h-14.01v-45.89h14.14c14.6 0 23.32 8.55 23.32 22.87 0 11.44-8.06 23.02-23.45 23.02m0-71.52h-41a2.45 2.45 0 00-2.45 2.46v92.24a2.45 2.45 0 002.45 2.45h41.13c32.31 0 53.2-19.1 53.2-48.65 0-29.47-20.94-48.5-53.33-48.5M474 104.27c-11.33 0-20.52 6.2-24.63 12.37-3-8.21-9.83-12.37-20.26-12.37-11.53 0-20.26 5.83-23.31 9.75v-5.56c0-1.36-1.1-2.45-2.45-2.45H382a2.44 2.44 0 00-2.44 2.45v65.45c0 1.34 1.1 2.44 2.44 2.44h21.34c1.35 0 2.45-1.1 2.45-2.44v-40.98c1.55-1.78 4.75-5.5 10.63-5.5 5.18 0 8.03 2.78 8.03 7.86v38.62c0 1.34 1.1 2.44 2.45 2.44h21.33c1.34 0 2.45-1.1 2.45-2.44v-40.96c1.54-1.8 4.75-5.53 10.77-5.53 5.1 0 8.02 2.86 8.02 7.87v38.62a2.45 2.45 0 002.45 2.44h21.34c1.35 0 2.44-1.1 2.44-2.44v-48.53c0-13.62-7.7-21.11-21.71-21.11m56.2 1.74h-21.33a2.45 2.45 0 00-2.46 2.46v65.44c0 1.34 1.1 2.44 2.46 2.44h21.33c1.34 0 2.45-1.1 2.45-2.44v-65.44c0-1.36-1.1-2.46-2.45-2.46M520.16 71.5c-8.11 0-14.72 6.6-14.72 14.7a14.72 14.72 0 0029.44 0c0-8.1-6.61-14.7-14.72-14.7m74.01 32.77c-13.6 0-21 6.1-24.19 9.75v-5.56c0-1.36-1.1-2.45-2.45-2.45h-21.34a2.45 2.45 0 00-2.44 2.45v65.45c0 1.34 1.1 2.44 2.44 2.44h21.34c1.35 0 2.45-1.1 2.45-2.44v-40.96c1.7-1.93 4.88-5.53 11.22-5.53 6.78 0 10.2 3.38 10.2 10.03v36.46c0 1.34 1.1 2.44 2.46 2.44h21.33c1.35 0 2.44-1.1 2.44-2.44v-47.5c0-13.87-8.77-22.14-23.46-22.14m-259.63 48.95a12.67 12.67 0 110-25.34 12.67 12.67 0 010 25.34m0-49.27a37.14 37.14 0 100 74.26 37.14 37.14 0 100-74.26m328.02 49.38a12.66 12.66 0 110-25.34 12.66 12.66 0 110 25.34m0-49.27a37.12 37.12 0 100 74.26 37.15 37.15 0 100-74.27m57.67-20.75l-.01-.04-.02-.05-.03-.15c-1.65-7.31-9.62-12.6-17.47-10.98-7.94 1.64-13.14 9.02-11.6 16.46 1.27 6.11 6.73 10.38 13.06 10.76-.4 2.52-2.63 4.75-4.47 5.8-1.24.7-.5 2.02-.5 2.02l1.64 2.42c.47.81 1.18 1.23 2.03.88 16.25-6.66 19.04-20.5 17.37-27.11m18.9 43.63c-.06-2.62 3.34-3.79 7.61-3.79 8.49 0 13.33 2.43 18.74 6a2.44 2.44 0 003.47-.84l7.2-12.52a2.44 2.44 0 00-.87-3.3c-5.92-3.54-14.59-8.09-29.64-8.09-21.7 0-33.32 10.21-31.38 24.9 3.41 25.79 43.56 16.95 42.93 25.76-.14 2.03-3.7 3.55-9.84 3.55-7.94 0-16.97-3.97-22.67-7.5a2.45 2.45 0 00-3.41.87l-8.32 13.87a2.45 2.45 0 00.76 3.24c7.98 5.39 20.87 9 32.08 9 21.73 0 34.24-9.32 34.25-24.65.01-28.29-40.73-19.51-40.9-26.5m63.54-22.49a14 14 0 016.9 1.81 12.78 12.78 0 015.21 5.17 14.07 14.07 0 01.05 14.02 13.22 13.22 0 01-5.17 5.2 14.13 14.13 0 01-13.98 0 13.12 13.12 0 01-5.18-5.2 14.08 14.08 0 01-1.87-6.97c0-2.45.64-4.8 1.91-7.05 1.25-2.24 3-3.98 5.2-5.17a14 14 0 016.93-1.8zm.02 2.75c-1.93 0-3.8.48-5.55 1.45a10.38 10.38 0 00-4.19 4.15 11.36 11.36 0 004.13 15.47 11.32 11.32 0 0011.21 0 11.31 11.31 0 00-.06-19.62 11.38 11.38 0 00-5.54-1.45zm-6.36 18.78V111.1h3.02a203 203 0 014.76.05c1 .09 1.78.28 2.34.58a4.2 4.2 0 011.94 3.57 4 4 0 01-1.06 2.76 4.76 4.76 0 01-2.96 1.4c.46.18.81.36 1.06.57.24.2.58.58 1 1.16a20 20 0 011.1 1.8l1.76 3h-3.74l-1.25-2.42c-.82-1.62-1.53-2.64-2.07-3.06a3.27 3.27 0 00-2.1-.63h-.78v6.11h-3.02zm3.02-8.45h1.23c1.74 0 2.82-.07 3.25-.21.42-.12.74-.38.98-.7.26-.35.37-.73.37-1.15 0-.41-.1-.78-.35-1.1a1.96 1.96 0 00-1.02-.71c-.44-.13-1.5-.2-3.23-.2h-1.23v4.07z"
      clipRule="evenodd"
    ></path>
  </svg>
);

export const DominosLogoBlue: React.FC<SvgProps> = ({
  className,
  width = 176,
  height = 38,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    enableBackground="new 0 0 817 182"
    viewBox="0 0 817 182"
    width={width}
    height={height}
    className={className}
  >
    <g transform="translate(0 2.91)">
      <defs>
        <filter
          id="astrap-blue"
          width="176.18"
          height="176.09"
          x="0"
          y=".74"
          filterUnits="userSpaceOnUse"
        >
          <feColorMatrix values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 1 0"></feColorMatrix>
        </filter>
        <style>{`:root{--neutral-white:rgba(245, 245, 245, 1);--primary-blue--brand-blue:rgba(0, 100, 145, 1);--primary-red-brand-red:rgba(227, 24, 55, 1)}`}</style>
      </defs>
      <mask
        id="bstrap-blue"
        width="176.18"
        height="176.09"
        x="0"
        y=".74"
        maskUnits="userSpaceOnUse"
      >
        <g filter="url(#astrap-blue)">
          <path
            fill="var(--neutral-white)"
            fillRule="evenodd"
            d="M0 .74h176.18v176.09H0z"
            clipRule="evenodd"
          ></path>
        </g>
      </mask>
      <path
        fill="var(--neutral-white)"
        fillRule="evenodd"
        d="M173.06 51.7L125.2 3.87a10.63 10.63 0 00-7.57-3.13c-2.85 0-5.54 1.1-7.56 3.13L69.94 43.98 3.13 110.74a10.7 10.7 0 000 15.12l47.86 47.84a10.63 10.63 0 007.56 3.13c2.86 0 5.54-1.11 7.56-3.13L126 113.85l47.06-47.04a10.7 10.7 0 000-15.11"
        clipRule="evenodd"
        mask="url(#bstrap-blue)"
      ></path>
    </g>
    <path
      fill="var(--primary-red-brand-red)"
      fillRule="evenodd"
      d="M127.58 71.84a13.86 13.86 0 11-19.6-19.6 13.86 13.86 0 0119.6 19.6m42.1-13.83L121.8 10.18a5.9 5.9 0 00-8.35 0L61.7 61.9l56.21 56.18 51.75-51.72a5.9 5.9 0 000-8.35"
      clipRule="evenodd"
    ></path>
    <path
      fill="var(--primary-blue--brand-blue)"
      fillRule="evenodd"
      d="M69.04 111.78a13.8 13.8 0 019.8-4.06c3.54 0 7.1 1.35 9.8 4.06a13.77 13.77 0 010 19.59 13.9 13.9 0 01-19.6 0 13.81 13.81 0 010-19.59zm-21.08 19.04a13.92 13.92 0 01-19.6 0 13.85 13.85 0 1123.66-9.8c0 3.54-1.36 7.1-4.06 9.8zM6.51 117.04a5.89 5.89 0 000 8.35l47.86 47.84a5.92 5.92 0 008.36 0l51.72-51.7-56.21-56.18-51.73 51.7z"
      clipRule="evenodd"
    ></path>
    <path
      fill="#006491"
      fillRule="evenodd"
      d="M108.8 149.3a14 14 0 016.9 1.82 12.8 12.8 0 015.21 5.16 14.02 14.02 0 01.05 14.02 13.2 13.2 0 01-5.17 5.2 14.14 14.14 0 01-13.98 0 13.09 13.09 0 01-5.18-5.2 14.08 14.08 0 01-1.87-6.97c0-2.45.64-4.8 1.91-7.05 1.25-2.24 3-3.98 5.2-5.16a14 14 0 016.93-1.81zm.02 2.76c-1.93 0-3.8.47-5.55 1.44a10.34 10.34 0 00-4.19 4.15 11.36 11.36 0 004.13 15.47 11.32 11.32 0 0011.21 0 11.3 11.3 0 004.14-15.47 11.33 11.33 0 00-9.74-5.6zm-6.36 18.78v-14.9h3.02a203 203 0 014.76.05c1 .09 1.78.28 2.34.58a4.2 4.2 0 011.94 3.57c0 1.07-.36 1.98-1.06 2.76a4.76 4.76 0 01-2.96 1.4c.46.18.81.36 1.06.57.24.2.58.58 1 1.16.11.13.48.73 1.10 1.80l1.76 3h-3.75l-1.24-2.42c-.82-1.62-1.53-2.64-2.07-3.06a3.3 3.3 0 00-2.1-.63h-.78v6.12h-3.02zm3.02-8.46h1.22c1.75 0 2.83-.06 3.26-.21.42-.12.74-.38.98-.70.26-.35.37-.73.37-1.15 0-.41-.1-.78-.35-1.1a2 2 0 00-1.02-.71c-.44-.13-1.5-.2-3.24-.2h-1.22v4.07zm133.45-11.65h-14.01v-45.89h14.14c14.6 0 23.32 8.55 23.32 22.87 0 11.44-8.06 23.02-23.45 23.02m0-71.52h-41a2.45 2.45 0 00-2.45 2.46v92.24a2.45 2.45 0 002.45 2.45h41.13c32.31 0 53.2-19.1 53.2-48.65 0-29.47-20.94-48.5-53.33-48.5M474 104.27c-11.33 0-20.52 6.2-24.63 12.37-3-8.21-9.83-12.37-20.26-12.37-11.53 0-20.26 5.83-23.31 9.75v-5.56c0-1.36-1.1-2.45-2.45-2.45H382a2.44 2.44 0 00-2.44 2.45v65.45c0 1.34 1.10 2.44 2.44 2.44h21.34c1.35 0 2.45-1.1 2.45-2.44v-40.98c1.55-1.78 4.75-5.5 10.63-5.5 5.18 0 8.03 2.78 8.03 7.86v38.62c0 1.34 1.1 2.44 2.45 2.44h21.33c1.34 0 2.45-1.1 2.45-2.44v-40.96c1.54-1.8 4.75-5.53 10.77-5.53 5.1 0 8.02 2.86 8.02 7.87v38.62a2.45 2.45 0 002.45 2.44h21.34c1.35 0 2.44-1.1 2.44-2.44v-48.53c0-13.62-7.7-21.11-21.71-21.11m56.2 1.74h-21.33a2.45 2.45 0 00-2.46 2.46v65.44c0 1.34 1.1 2.44 2.46 2.44h21.33c1.34 0 2.45-1.1 2.45-2.44v-65.44c0-1.36-1.1-2.46-2.45-2.46M520.16 71.5c-8.11 0-14.72 6.6-14.72 14.7a14.72 14.72 0 0029.44 0c0-8.1-6.61-14.7-14.72-14.7m74.01 32.77c-13.6 0-21 6.1-24.19 9.75v-5.56c0-1.36-1.1-2.45-2.45-2.45h-21.34a2.45 2.45 0 00-2.44 2.45v65.45c0 1.34 1.1 2.44 2.44 2.44h21.34c1.35 0 2.45-1.1 2.45-2.44v-40.96c1.7-1.93 4.88-5.53 11.22-5.53 6.78 0 10.2 3.38 10.2 10.03v36.46c0 1.34 1.1 2.44 2.46 2.44h21.33c1.35 0 2.44-1.1 2.44-2.44v-47.5c0-13.87-8.77-22.14-23.46-22.14m-259.63 48.95a12.67 12.67 0 110-25.34 12.67 12.67 0 010 25.34m0-49.27a37.14 37.14 0 100 74.26 37.14 37.14 0 100-74.26m328.02 49.38a12.66 12.66 0 110-25.34 12.66 12.66 0 110 25.34m0-49.27a37.12 37.12 0 100 74.26 37.15 37.15 0 100-74.27m57.67-20.75l-.01-.04-.02-.05-.03-.15c-1.65-7.31-9.62-12.6-17.47-10.98-7.94 1.64-13.14 9.02-11.6 16.46 1.27 6.11 6.73 10.38 13.06 10.76-.4 2.52-2.63 4.75-4.47 5.8-1.24.7-.5 2.02-.5 2.02l1.64 2.42c.47.81 1.18 1.23 2.03.88 16.25-6.66 19.04-20.5 17.37-27.11m18.9 43.63c-.06-2.62 3.34-3.79 7.61-3.79 8.49 0 13.33 2.43 18.74 6a2.44 2.44 0 003.47-.84l7.2-12.52a2.44 2.44 0 00-.87-3.3c-5.92-3.54-14.59-8.09-29.64-8.09-21.7 0-33.32 10.21-31.38 24.9 3.41 25.79 43.56 16.95 42.93 25.76-.14 2.03-3.7 3.55-9.84 3.55-7.94 0-16.97-3.97-22.67-7.5a2.45 2.45 0 00-3.41.87l-8.32 13.87a2.45 2.45 0 00.76 3.24c7.98 5.39 20.87 9 32.08 9 21.73 0 34.24-9.32 34.25-24.65.01-28.29-40.73-19.51-40.9-26.5m63.54-22.49a14 14 0 016.9 1.81 12.78 12.78 0 015.21 5.17 14.07 14.07 0 01.05 14.02 13.22 13.22 0 01-5.17 5.2 14.13 14.13 0 01-13.98 0 13.12 13.12 0 01-5.18-5.2 14.08 14.08 0 01-1.87-6.97c0-2.45.64-4.8 1.91-7.05 1.25-2.24 3-3.98 5.2-5.17a14 14 0 016.93-1.8zm.02 2.75c-1.93 0-3.8.48-5.55 1.45a10.38 10.38 0 00-4.19 4.15 11.36 11.36 0 004.13 15.47 11.32 11.32 0 0011.21 0 11.31 11.31 0 00-.06-19.62 11.38 11.38 0 00-5.54-1.45zm-6.36 18.78V111.1h3.02a203 203 0 014.76.05c1 .09 1.78.28 2.34.58a4.2 4.2 0 011.94 3.57 4 4 0 01-1.06 2.76 4.76 4.76 0 01-2.96 1.4c.46.18.81.36 1.06.57.24.2.58.58 1 1.16a20 20 0 011.1 1.8l1.76 3h-3.74l-1.25-2.42c-.82-1.62-1.53-2.64-2.07-3.06a3.27 3.27 0 00-2.1-.63h-.78v6.11h-3.02zm3.02-8.45h1.23c1.74 0 2.82-.07 3.25-.21.42-.12.74-.38.98-.70.26-.35.37-.73.37-1.15 0-.41-.1-.78-.35-1.1a1.96 1.96 0 00-1.02-.71c-.44-.13-1.5-.2-3.23-.2h-1.23v4.07z"
      clipRule="evenodd"
    ></path>
  </svg>
);

export const GreenCircleIcon: React.FC<SvgProps> = ({
  className = "text-dominos-green",
  width = 24,
  height = 24,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    className={className}
  >
    <circle cx="12" cy="12" r="12" fill="currentColor" />
    <path
      d="M12 7.5C10.34 7.5 9 8.84 9 10.5C9 12.16 10.34 13.5 12 13.5C13.66 13.5 15 12.16 15 10.5C15 8.84 13.66 7.5 12 7.5Z"
      fill="white"
    />
  </svg>
);

export const PlusIcon: React.FC<SvgProps> = ({
  className,
  width = 25,
  height = 25,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 25 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <rect x="11" y="4" width="3" height="17" fill="white" />
    <rect x="4" y="11" width="17" height="3" fill="white" />
  </svg>
);

export const ChevronDownIcon: React.FC<SvgProps> = ({
  className = "inline ml-2 w-5 h-5",
  width,
  height,
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox="0 0 34 29"
    fill="none"
  >
    <path
      d="M30.6838 8.25959C30.3703 8.00041 29.9509 7.85547 29.5143 7.85547C29.0777 7.85547 28.6583 8.00041 28.3448 8.25959L16.9308 17.8054L5.6555 8.37438C5.34211 8.11556 4.92298 7.97085 4.48675 7.97085C4.05052 7.97085 3.63139 8.11556 3.318 8.37438C3.16481 8.50171 3.04307 8.65391 2.95994 8.822C2.87681 8.9901 2.83398 9.17069 2.83398 9.35313C2.83398 9.53557 2.87681 9.71616 2.95994 9.88426C3.04307 10.0524 3.16481 10.2046 3.318 10.3319L15.762 20.7429C16.0751 21.0027 16.4902 21.1477 16.9308 21.1477C17.3728 21.1477 17.7878 21.0027 18.1023 20.7429L30.6823 10.2183C30.8355 10.091 30.9573 9.93877 31.0404 9.77068C31.1235 9.60258 31.1664 9.42199 31.1664 9.23955C31.1664 9.05711 31.1235 8.87652 31.0404 8.70842C30.9573 8.54032 30.8355 8.38812 30.6823 8.2608L30.6838 8.25959Z"
      fill="currentColor"
    />
  </svg>
);

export const ChevronUpIcon: React.FC<SvgProps> = ({
  className = "inline ml-2 w-5 h-5",
  width,
  height,
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox="0 0 34 29"
    fill="none"
  >
    <path
      d="M3.31623 20.7404C3.62973 20.9996 4.04907 21.1445 4.48567 21.1445C4.92227 21.1445 5.34161 20.9996 5.65511 20.7404L17.0692 11.1946L28.3445 20.6256C28.6579 20.8844 29.077 21.0291 29.5132 21.0291C29.9495 21.0291 30.3686 20.8844 30.682 20.6256C30.8352 20.4983 30.9569 20.3461 31.0401 20.178C31.1232 20.0099 31.166 19.8293 31.166 19.6469C31.166 19.4644 31.1232 19.2838 31.0401 19.1157C30.9569 18.9476 30.8352 18.7954 30.682 18.6681L18.238 8.25711C17.9249 7.99729 17.5098 7.85229 17.0692 7.85229C16.6272 7.85229 16.2122 7.99729 15.8977 8.25711L3.31773 18.7817C3.16454 18.909 3.04272 19.0612 2.95959 19.2293C2.87646 19.3974 2.83363 19.578 2.83363 19.7604C2.83363 19.9429 2.87646 20.1235 2.95959 20.2916C3.04272 20.4597 3.16454 20.6119 3.31773 20.7392L3.31623 20.7404Z"
      fill="currentColor"
    />
  </svg>
);

export const RibbonEnd: React.FC<RibbonEndProps> = ({
  colors,
  side,
  className,
}) => {
  if (side === "left") {
    return (
      <svg
        width="100"
        height="100"
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`w-[54px] h-[54px] md:w-[100px] md:h-[100px] ${className || ""}`}
      >
        <g clipPath="url(#clip0_hero_left)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M67 0H37V8H0L10.8 37L0 66H58V58H67V0Z"
            fill={colors.main}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M26.9004 8.09961V58.0996L57.9004 66.0996L36.9004 58.0996V8.09961H26.9004Z"
            fill={colors.dark}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M36.9004 58.0996L57.9004 66.0996V58.0996H36.9004Z"
            fill={colors.shadow}
          />
        </g>
        <defs>
          <clipPath id="clip0_hero_left">
            <rect width="100" height="100" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  }

  return (
    <svg
      width="100"
      height="100"
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`w-[54px] h-[54px] md:w-[100px] md:h-[100px] ${className || ""}`}
    >
      <g clipPath="url(#clip0_hero_right)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0 0H30V8H67L56.2 37L67 66H9V58H0V0Z"
          fill={colors.main}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M40 8V58L9 66L30 58V8H40Z"
          fill={colors.dark}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M30 58L9 66V58H30Z"
          fill={colors.shadow}
        />
      </g>
      <defs>
        <clipPath id="clip0_hero_right">
          <rect width="100" height="100" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const DealSvg: React.FC<SvgProps> = ({
  className,
  width = 140,
  height = 65,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 140 65"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M26.5728 45.2958C23.9215 45.8914 21.3351 46.535 18.8207 47.2242V30.4953H18.7919C11.9643 32.3733 5.66037 34.5947 0 37.1067L7.20455 47.9135L0 63.5233C7.75209 60.0844 16.7145 57.1881 26.5728 54.9763V45.2958Z"
      fill="#006491"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M113.182 45.2958C115.833 45.8914 118.417 46.535 120.934 47.2242V30.4953H120.963C127.79 32.3733 134.094 34.5947 139.755 37.1067L132.55 47.9135L139.755 63.5233C132 60.0844 123.04 57.1881 113.182 54.9763V45.2958Z"
      fill="#006491"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M26.5728 54.9771L18.8207 47.225C21.3351 46.5333 23.9215 45.8897 26.5728 45.2965V54.9771Z"
      fill="#CCCCCC"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M69.8774 14C51.196 14.245 33.7586 16.7065 18.8212 20.8083V30.496V47.225C21.3355 46.5333 23.922 45.8897 26.5732 45.2965C39.6663 42.3571 54.3396 40.6208 69.8774 40.4167C85.4128 40.6208 100.088 42.3571 113.181 45.2965C115.833 45.8897 118.417 46.5333 120.934 47.225V30.496V20.8083C105.996 16.7065 88.5588 14.245 69.8774 14Z"
      fill="#006491"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M56.1004 32.2856C56.8544 32.2328 57.2891 31.6372 57.2291 30.7967L56.7151 23.6017C56.6527 22.7612 56.1412 22.2353 55.3847 22.2881L54.5898 22.3457L55.3031 32.3433L56.1004 32.2856ZM51.6912 19.9971L55.2046 19.7449C57.9615 19.548 59.2896 21.4692 59.4625 23.8803L59.9092 30.1315C60.0821 32.5426 59.0398 34.6319 56.2805 34.8288L52.7695 35.081L51.6912 19.9971Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M60.6992 19.3494L67.2938 19.138L67.3755 21.6885L63.5066 21.8133L63.6123 25.0554L66.2275 24.9713L66.3092 27.5217L63.6939 27.6058L63.8308 31.8445L67.6997 31.7196L67.7813 34.27L61.1868 34.4837L60.6992 19.3494Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M74.1211 28.6601L73.3478 23.7562L72.3896 28.6264L74.1211 28.6601ZM71.9405 19.1452L74.9232 19.2029L77.7978 34.3949L75.0313 34.342L74.5269 31.2177L71.8877 31.1696L71.2705 34.2724L68.5015 34.222L71.9405 19.1452Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M79.4729 19.3892L82.1914 19.5646L81.3821 32.1269L85.0276 32.3622L84.8643 34.9078L78.4978 34.4996L79.4729 19.3892Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M113.181 54.9771L120.933 47.225C118.418 46.5333 115.832 45.8897 113.181 45.2965V54.9771Z"
      fill="#CCCCCC"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M113.181 54.9771L120.933 47.225V30.496L125.837 31.9225V48.2552L113.181 54.9771Z"
      fill="#005277"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M26.5728 54.9771L18.8207 47.225V30.496L13.9192 31.9225V48.2552L26.5728 54.9771Z"
      fill="#005277"
    />
  </svg>
);

export const VoucherSvg: React.FC<SvgProps> = ({
  className,
  width = 140,
  height = 65,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 140 65"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <g clipPath="url(#clip0_voucher)">
      <mask
        id="mask0_voucher"
        style={{ maskType: "alpha" }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="31"
        width="27"
        height="34"
      >
        <path d="M0 31.5935H26.6196V64.6775H0V31.5935Z" fill="white" />
      </mask>
      <g mask="url(#mask0_voucher)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M26.6196 46.4185C23.9646 47.0145 21.3736 47.6595 18.8546 48.3515V31.5935H18.8256C11.9866 33.4745 5.6706 35.6995 -0.000396729 38.2145L7.2176 49.0405L-0.000396729 64.6775C7.7666 61.2325 16.7436 58.3325 26.6196 56.1155V46.4185Z"
          fill="#006491"
        />
      </g>
      <mask
        id="mask1_voucher"
        style={{ maskType: "alpha" }}
        maskUnits="userSpaceOnUse"
        x="113"
        y="31"
        width="27"
        height="34"
      >
        <path d="M113.38 31.5935H140V64.6775H113.38V31.5935Z" fill="white" />
      </mask>
      <g mask="url(#mask1_voucher)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M113.38 46.4185C116.035 47.0145 118.626 47.6595 121.145 48.3515V31.5935H121.174C128.013 33.4745 134.329 35.6995 140 38.2145L132.782 49.0405L140 64.6775C132.233 61.2325 123.256 58.3325 113.38 56.1155V46.4185Z"
          fill="#006491"
        />
      </g>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M26.6201 56.1172L18.8541 48.3512C21.3741 47.6592 23.9641 47.0142 26.6201 46.4182V56.1172Z"
        fill="#CCCCCC"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M113.38 56.1172L121.146 48.3512C118.626 47.6592 116.036 47.0142 113.38 46.4182V56.1172Z"
        fill="#CCCCCC"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M113.38 56.1172L121.146 48.3512V31.5942L126.056 33.0222V49.3822L113.38 56.1172Z"
        fill="#005277"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M26.6201 56.1172L18.8541 48.3512V31.5942L13.9441 33.0222V49.3822L26.6201 56.1172Z"
        fill="#005277"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M84.4814 15.8169C84.4834 15.7549 84.4974 15.6929 84.4974 15.6309C84.4974 12.4889 81.5354 9.7539 77.1504 8.2999C75.1784 7.3059 72.7004 6.7079 70.0004 6.7079C67.3884 6.7079 64.9864 7.2699 63.0464 8.2059C58.5094 9.6339 55.4294 12.4229 55.4294 15.6309C55.4294 15.6949 55.4424 15.7579 55.4454 15.8219C42.2354 16.8349 29.8564 18.9329 18.8554 21.9549V31.6599V48.4179C21.3744 47.7259 23.9644 47.0809 26.6194 46.4839C39.7354 43.5409 54.4364 41.8019 70.0004 41.5959C85.5644 41.8019 100.264 43.5409 113.38 46.4839C116.035 47.0809 118.626 47.7259 121.145 48.4179V31.6599V21.9549C110.123 18.9279 97.7194 16.8269 84.4814 15.8169Z"
        fill="#006491"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M36.9238 22.8623L39.7138 22.4913L42.6448 31.2063L43.1888 22.0283L45.9788 21.6553L44.7628 37.1043L42.1458 37.4533L36.9238 22.8623Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M52.9248 32.544L52.2118 24.793C52.1408 24.019 51.5798 23.592 50.9338 23.652C50.2888 23.711 49.8148 24.232 49.8858 25.008L50.5998 32.758C50.6708 33.534 51.2318 33.96 51.8768 33.9C52.5228 33.841 52.9968 33.32 52.9248 32.544ZM47.8858 33.008L47.1738 25.257C46.9548 22.867 48.5608 21.222 50.6928 21.025C52.8238 20.83 54.7038 22.155 54.9248 24.544L55.6368 32.295C55.8568 34.685 54.2498 36.331 52.1188 36.526C49.9868 36.722 48.1068 35.398 47.8858 33.008Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M56.7251 32.2202L56.1231 20.7692L58.8451 20.6262L59.4471 32.0772C59.4881 32.8542 60.0321 33.3022 60.6791 33.2682C61.3291 33.2342 61.8211 32.7322 61.7801 31.9532L61.1781 20.5042L63.9001 20.3612L64.5021 31.8102C64.6291 34.2082 62.9571 35.7912 60.8191 35.9032C58.6791 36.0152 56.8521 34.6182 56.7251 32.2202Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M65.4951 31.1861L65.473 24.4781C65.4641 22.0551 66.6471 20.0371 69.4171 20.0281C72.1881 20.0191 73.3861 22.0281 73.3921 24.4521L73.3951 25.4241L70.6701 25.4341L70.6651 24.0711C70.6621 23.2281 70.1841 22.6661 69.4271 22.6691C68.6701 22.6711 68.1961 23.2361 68.1981 24.0801L68.2241 31.5661C68.2261 32.4101 68.7031 32.9711 69.4621 32.9691C70.2191 32.9671 70.6931 32.4021 70.6911 31.5581L70.6861 30.1951L73.4121 30.1861L73.4161 31.1591C73.4231 33.5831 72.2401 35.5991 69.4701 35.6081C66.7001 35.6181 65.5031 33.6101 65.4951 31.1861Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M75.1064 20.3355L77.8294 20.4695L77.5434 26.2585L79.8764 26.3745L80.1624 20.5845L82.8834 20.7185L82.1394 35.8395L79.4184 35.7065L79.7524 28.9235L77.4184 28.8085L77.0854 35.5905L74.3624 35.4575L75.1064 20.3355Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M84.627 20.833L91.207 21.403L90.986 23.949L87.125 23.614L86.843 26.85L89.454 27.075L89.233 29.621L86.623 29.394L86.256 33.62L90.118 33.956L89.897 36.5L83.32 35.93L84.627 20.833Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M95.0142 28.3706C96.2162 28.5196 96.8882 28.0376 97.0332 26.8576L97.0822 26.4716C97.2302 25.2906 96.6902 24.7226 95.4882 24.5726L95.2092 24.5376L94.7352 28.3356L95.0142 28.3706ZM92.8332 21.5826L96.0312 21.9806C99.1412 22.3696 100.087 24.3836 99.7932 26.7436L99.7702 26.9376C99.6432 27.9456 99.0982 29.3376 97.5732 29.8656C98.2752 30.3686 98.5562 31.0786 98.4742 32.2446L98.1242 37.4956L95.3992 37.1566L95.8072 32.3256C95.8942 31.2696 95.1852 30.8316 94.4372 30.7386L93.6622 36.9396L90.9582 36.6016L92.8332 21.5826Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_voucher">
        <rect width="140" height="65" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

/**
 * Six-slice pizza loader matching Domino's UK `base-default-spinner` exactly —
 * artwork lifted from their inline SVG (viewBox 0 0 275 275). Each slice fades
 * out while sliding away from the centre, then fades back in as it returns,
 * one after another clockwise. Keyframes and timings live in index.css under
 * `.pizza-spinner`; size it with a className (their size is 10rem).
 */
export const PizzaSpinner: React.FC<SvgProps> = ({
  className,
  width = 275,
  height = 275,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 275 275"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`pizza-spinner ${className ?? ""}`}
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M91.1126 54.7265C120.483 38.574 154.841 39.6203 182.373 54.7165L177.895 62.4723C165.672 55.7803 151.646 51.9765 136.74 51.9765C124.898 51.9765 113.613 54.3718 103.351 58.7146C102.333 59.145 101.858 60.3186 102.288 61.3359C102.719 62.3531 103.892 62.8288 104.909 62.3983C114.687 58.2611 125.442 55.9765 136.74 55.9765C150.924 55.9765 164.263 59.5862 175.894 65.9373L136.74 133.747L91.1126 54.7265ZM180.32 66.2718L138.489 138.716C138.4 138.878 138.287 139.029 138.153 139.163C137.98 139.335 137.78 139.472 137.563 139.569C137.298 139.69 137.017 139.747 136.74 139.747C136.463 139.747 136.182 139.69 135.917 139.569C135.7 139.472 135.5 139.335 135.327 139.163C135.193 139.029 135.08 138.878 134.991 138.716L86.638 54.9765C86.4584 54.6654 86.3718 54.3261 86.3697 53.9908C86.3654 53.2952 86.7246 52.6169 87.3702 52.2443C119.023 33.9731 156.575 35.2117 186.118 52.2335C187.076 52.7849 187.404 54.0078 186.853 54.9649C186.832 55.0009 186.811 55.036 186.788 55.0702L180.42 66.0988C180.405 66.1286 180.388 66.1583 180.371 66.1876C180.355 66.2163 180.338 66.2443 180.32 66.2718ZM118.18 74.9664C118.18 77.1093 116.443 78.8464 114.3 78.8464C112.157 78.8464 110.42 77.1093 110.42 74.9664C110.42 72.8236 112.157 71.0864 114.3 71.0864C116.443 71.0864 118.18 72.8236 118.18 74.9664ZM163.05 74.9664C163.05 77.1093 161.313 78.8464 159.17 78.8464C157.027 78.8464 155.29 77.1093 155.29 74.9664C155.29 72.8236 157.027 71.0864 159.17 71.0864C161.313 71.0864 163.05 72.8236 163.05 74.9664ZM136.74 114.926C138.883 114.926 140.62 113.189 140.62 111.046C140.62 108.904 138.883 107.166 136.74 107.166C134.597 107.166 132.86 108.904 132.86 111.046C132.86 113.189 134.597 114.926 136.74 114.926ZM136.74 72.9664C129.53 72.9664 125.243 76.7221 123.393 81.1329C122.493 83.2804 122.178 85.5582 122.347 87.5863C122.511 89.5626 123.162 91.5527 124.472 92.8863C125.626 94.0662 127.298 94.002 128.255 93.8097C128.844 93.6914 129.524 93.4566 130.14 93.0751C130.124 93.2557 130.109 93.4362 130.098 93.6156C129.995 95.2151 130.068 97.2228 131.053 98.8743C132.159 100.728 134.118 101.687 136.74 101.667C139.36 101.687 141.318 100.727 142.423 98.8737C143.407 97.2228 143.481 95.2157 143.378 93.6164C143.367 93.436 143.352 93.2544 143.336 93.0728C143.953 93.4556 144.635 93.6911 145.225 93.8097C146.181 94.002 147.853 94.0662 149.008 92.8864C150.317 91.5511 150.969 89.5602 151.133 87.582C151.302 85.5536 150.987 83.276 150.087 81.129C148.237 76.7188 143.949 72.9664 136.74 72.9664ZM130.802 88.1761C130.802 88.1758 130.803 88.181 130.804 88.1922C130.802 88.1819 130.802 88.1763 130.802 88.1761ZM142.677 88.1774C142.678 88.1776 142.677 88.1832 142.676 88.1936C142.677 88.1824 142.677 88.1773 142.677 88.1774ZM127.184 89.9201C126.809 89.4365 126.44 88.5407 126.333 87.2541C126.216 85.8547 126.435 84.2225 127.082 82.6799C128.327 79.7107 131.19 76.9664 136.74 76.9664C142.291 76.9664 145.153 79.709 146.398 82.6764C147.045 84.2182 147.264 85.8499 147.147 87.2499C147.04 88.5357 146.671 89.4335 146.296 89.9201C146.225 89.9194 146.13 89.9116 146.013 89.8881C145.82 89.8493 145.637 89.7812 145.502 89.7069C145.455 89.6812 145.422 89.6598 145.401 89.6448L145.394 89.6283C145.366 89.564 145.306 89.4251 145.201 89.1618L145.169 89.0816C145.028 88.7269 144.831 88.2306 144.589 87.7626C144.338 87.2779 143.94 86.6187 143.313 86.1207C142.596 85.5505 141.619 85.2317 140.518 85.5055L140.515 85.5061C139.701 85.7096 139.204 86.2784 138.958 86.8014C138.745 87.2545 138.696 87.7043 138.681 87.9938C138.651 88.5837 138.739 89.2652 138.826 89.8423C138.88 90.1983 138.939 90.5563 138.999 90.9171C139.159 91.8784 139.322 92.8589 139.387 93.8715C139.477 95.2846 139.325 96.2588 138.987 96.826C138.772 97.1859 138.319 97.6814 136.759 97.6665L136.738 97.6664L136.721 97.6665C135.157 97.6814 134.703 97.1851 134.489 96.8255C134.151 96.2588 133.999 95.2852 134.09 93.8723C134.155 92.8593 134.318 91.8782 134.479 90.9163L134.479 90.9155C134.539 90.5558 134.598 90.1986 134.652 89.8436C134.739 89.2666 134.828 88.5851 134.799 87.9952C134.784 87.7057 134.736 87.2557 134.522 86.8024C134.276 86.2788 133.779 85.7096 132.965 85.5061L132.962 85.5055C131.861 85.2317 130.884 85.5505 130.167 86.1207C129.54 86.6187 129.142 87.2779 128.891 87.7626C128.649 88.2306 128.452 88.7269 128.311 89.0817L128.279 89.1618C128.174 89.4251 128.114 89.564 128.086 89.6283L128.079 89.6448C128.058 89.6598 128.025 89.6812 127.978 89.7069C127.843 89.7812 127.66 89.8493 127.467 89.8881C127.35 89.9117 127.254 89.9195 127.184 89.9201ZM146.416 89.9117C146.419 89.9108 146.421 89.9105 146.421 89.9105C146.421 89.9106 146.419 89.911 146.416 89.9117ZM127.059 89.9105C127.059 89.9105 127.061 89.9108 127.064 89.9117C127.061 89.911 127.059 89.9106 127.059 89.9105Z"
      fill="currentColor"
      className="pizza-spinner__slice pizza-spinner__slice-1"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M42.0207 135.756C42.7147 102.248 60.8086 73.0105 87.6427 56.7166L92.1207 64.4718C80.2118 71.7086 69.9017 81.9517 62.4478 94.8666C56.5315 105.113 52.9676 116.09 51.5952 127.15C51.4592 128.246 52.2376 129.245 53.3338 129.381C54.4299 129.517 55.4288 128.739 55.5648 127.643C56.8725 117.103 60.2684 106.64 65.912 96.8665L65.9122 96.8661C73.0052 84.5765 82.8035 74.8313 94.1214 67.9367L133.281 135.756H42.0207ZM136.74 139.756C137.829 139.756 138.714 138.887 138.739 137.804C138.75 137.445 138.664 137.08 138.472 136.746L90.102 52.9763C90.0113 52.8192 89.9024 52.679 89.7796 52.5569C89.155 51.927 88.1621 51.7702 87.3583 52.2354C57.8559 69.3081 38 101.214 38 137.756C38 138.497 38.4022 139.143 39 139.489C39.2942 139.659 39.6357 139.756 40 139.756L136.74 139.756ZM93.58 90.8164C95.7229 90.8164 97.46 89.0792 97.46 86.9364C97.46 84.7935 95.7229 83.0564 93.58 83.0564C91.4371 83.0564 89.7 84.7935 89.7 86.9364C89.7 89.0792 91.4371 90.8164 93.58 90.8164ZM83.29 111.956C83.29 107.102 87.2254 103.166 92.08 103.166C96.9346 103.166 100.87 107.102 100.87 111.956C100.87 116.811 96.9346 120.746 92.08 120.746C87.2254 120.746 83.29 116.811 83.29 111.956ZM92.08 99.1663C85.0163 99.1663 79.29 104.893 79.29 111.956C79.29 119.02 85.0163 124.746 92.08 124.746C99.1437 124.746 104.87 119.02 104.87 111.956C104.87 104.893 99.1437 99.1663 92.08 99.1663ZM75.03 125.786C75.03 127.929 73.2929 129.666 71.15 129.666C69.0072 129.666 67.27 127.929 67.27 125.786C67.27 123.644 69.0072 121.906 71.15 121.906C73.2929 121.906 75.03 123.644 75.03 125.786ZM117.49 124.406C117.49 126.549 115.753 128.286 113.61 128.286C111.467 128.286 109.73 126.549 109.73 124.406C109.73 122.263 111.467 120.526 113.61 120.526C115.753 120.526 117.49 122.263 117.49 124.406Z"
      fill="currentColor"
      className="pizza-spinner__slice pizza-spinner__slice-6"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M181.35 64.4723L185.827 56.7167C212.661 73.0106 230.755 102.248 231.449 135.756H140.198L179.35 67.9374C190.667 74.8319 200.465 84.5769 207.558 96.8661L207.558 96.8665C213.202 106.64 216.598 117.103 217.905 127.643C218.041 128.739 219.04 129.517 220.136 129.381C221.232 129.245 222.011 128.246 221.875 127.15C220.502 116.09 216.938 105.112 211.022 94.8663C203.568 81.9518 193.259 71.709 181.35 64.4723ZM135.008 136.746C134.815 137.08 134.73 137.446 134.741 137.805C134.766 138.887 135.652 139.756 136.74 139.756L233.47 139.756H233.47C234.437 139.756 235.243 139.071 235.429 138.159C235.456 138.029 235.47 137.894 235.47 137.756V137.747C235.466 101.208 215.611 69.3066 186.112 52.2354C185.308 51.7701 184.315 51.927 183.69 52.5573C183.567 52.6794 183.459 52.8195 183.368 52.9765L135.008 136.746ZM179.89 90.8164C182.033 90.8164 183.77 89.0792 183.77 86.9364C183.77 84.7935 182.033 83.0564 179.89 83.0564C177.747 83.0564 176.01 84.7935 176.01 86.9364C176.01 89.0792 177.747 90.8164 179.89 90.8164ZM172.6 111.956C172.6 107.102 176.535 103.166 181.39 103.166C186.245 103.166 190.18 107.102 190.18 111.956C190.18 116.811 186.245 120.746 181.39 120.746C176.535 120.746 172.6 116.811 172.6 111.956ZM181.39 99.1663C174.326 99.1663 168.6 104.893 168.6 111.956C168.6 119.02 174.326 124.746 181.39 124.746C188.454 124.746 194.18 119.02 194.18 111.956C194.18 104.893 188.454 99.1663 181.39 99.1663ZM206.2 125.786C206.2 127.929 204.463 129.666 202.32 129.666C200.177 129.666 198.44 127.929 198.44 125.786C198.44 123.644 200.177 121.906 202.32 121.906C204.463 121.906 206.2 123.644 206.2 125.786ZM163.74 124.406C163.74 126.549 162.003 128.286 159.86 128.286C157.717 128.286 155.98 126.549 155.98 124.406C155.98 122.263 157.717 120.526 159.86 120.526C162.003 120.526 163.74 122.263 163.74 124.406Z"
      fill="currentColor"
      className="pizza-spinner__slice pizza-spinner__slice-2"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M134.991 136.776C135.08 136.615 135.193 136.464 135.327 136.33C135.5 136.158 135.7 136.021 135.917 135.923C136.182 135.803 136.463 135.746 136.74 135.746C137.017 135.746 137.298 135.803 137.563 135.923C137.78 136.021 137.98 136.158 138.153 136.33C138.287 136.464 138.4 136.615 138.489 136.776L186.842 220.516L186.842 220.516C187.394 221.473 187.067 222.696 186.11 223.248L186.11 223.248C154.457 241.52 116.905 240.281 87.3615 223.259C86.4044 222.708 86.0756 221.485 86.627 220.528C86.6478 220.492 86.6695 220.457 86.6922 220.422L93.0602 209.394C93.0755 209.364 93.0916 209.334 93.1086 209.305C93.1251 209.277 93.1422 209.249 93.1598 209.221L134.991 136.776ZM95.585 213.021C107.808 219.713 121.834 223.516 136.74 223.516C148.582 223.516 159.867 221.121 170.129 216.778C171.147 216.348 171.622 215.174 171.192 214.157C170.761 213.14 169.588 212.664 168.571 213.094C158.793 217.232 148.038 219.516 136.74 219.516C122.555 219.516 109.217 215.907 97.5857 209.556L136.74 141.746L182.367 220.766C152.997 236.919 118.639 235.872 91.1068 220.776L95.585 213.021ZM140.62 164.446C140.62 166.589 138.883 168.326 136.74 168.326C134.597 168.326 132.86 166.589 132.86 164.446C132.86 162.304 134.597 160.566 136.74 160.566C138.883 160.566 140.62 162.304 140.62 164.446ZM127.94 189.316C127.94 184.462 131.875 180.526 136.73 180.526C141.585 180.526 145.52 184.462 145.52 189.316C145.52 194.171 141.585 198.106 136.73 198.106C131.875 198.106 127.94 194.171 127.94 189.316ZM136.73 176.526C129.666 176.526 123.94 182.253 123.94 189.316C123.94 196.38 129.666 202.106 136.73 202.106C143.794 202.106 149.52 196.38 149.52 189.316C149.52 182.253 143.794 176.526 136.73 176.526ZM163.05 200.526C163.05 202.669 161.313 204.406 159.17 204.406C157.027 204.406 155.29 202.669 155.29 200.526C155.29 198.383 157.027 196.646 159.17 196.646C161.313 196.646 163.05 198.383 163.05 200.526ZM114.31 204.406C116.453 204.406 118.19 202.669 118.19 200.526C118.19 198.383 116.453 196.646 114.31 196.646C112.167 196.646 110.43 198.383 110.43 200.526C110.43 202.669 112.167 204.406 114.31 204.406Z"
      fill="currentColor"
      className="pizza-spinner__slice pizza-spinner__slice-4"
    />
    <g
      className="pizza-spinner__slice pizza-spinner__slice-5">
      <path
        d="M71.15 153.656C73.2929 153.656 75.03 151.919 75.03 149.776C75.03 147.633 73.2929 145.896 71.15 145.896C69.0072 145.896 67.27 147.633 67.27 149.776C67.27 151.919 69.0072 153.656 71.15 153.656Z"
        fill="currentColor"
      />
      <path
        d="M93.58 192.506C95.7229 192.506 97.46 190.769 97.46 188.626C97.46 186.483 95.7229 184.746 93.58 184.746C91.4371 184.746 89.7 186.483 89.7 188.626C89.7 190.769 91.4371 192.506 93.58 192.506Z"
        fill="currentColor"
      />
      <path
        d="M113.61 155.036C115.753 155.036 117.49 153.299 117.49 151.156C117.49 149.014 115.753 147.276 113.61 147.276C111.467 147.276 109.73 149.014 109.73 151.156C109.73 153.299 111.467 155.036 113.61 155.036Z"
        fill="currentColor"
      />
      <path
        d="M103.75 156.846C108.02 164.066 94.82 164.976 95.29 166.646C95.77 168.316 98.65 167.276 99.6 167.356C100.81 167.466 102.41 169.146 102.09 170.316C100.76 175.076 88.74 180.236 82.36 169.196C75.98 158.146 86.47 150.316 91.25 151.546C92.42 151.846 93.08 154.076 92.57 155.176C92.17 156.036 89.83 158.016 91.03 159.266C92.25 160.526 99.64 149.546 103.75 156.846Z"
        stroke="currentColor"
        strokeWidth={4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M40 135.806C41.1046 135.806 42 136.702 42 137.806C42 172.864 61.0441 203.478 89.3617 219.865C90.3178 220.419 90.6443 221.642 90.091 222.598C89.5378 223.554 88.3143 223.881 87.3583 223.327C57.8559 206.255 38 174.349 38 137.806C38 136.702 38.8954 135.806 40 135.806Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M38 137.806C38 136.702 38.8954 135.806 40 135.806H136.74C137.845 135.806 138.74 136.702 138.74 137.806C138.74 138.911 137.845 139.806 136.74 139.806H40C38.8954 139.806 38 138.911 38 137.806Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M137.74 136.084C138.697 136.637 139.024 137.86 138.472 138.816L90.102 222.586C89.5497 223.543 88.3265 223.871 87.3699 223.318C86.4134 222.766 86.0857 221.543 86.638 220.586L135.008 136.816C135.56 135.86 136.784 135.532 137.74 136.084Z"
        fill="currentColor"
      />
      <path
        d="M53.58 148.166C54.92 158.966 58.4 169.686 64.18 179.696C71.81 192.916 82.5 203.266 94.84 210.376"
        stroke="currentColor"
        strokeWidth={4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <g
      className="pizza-spinner__slice pizza-spinner__slice-3">
      <g>
        <path
          d="M202.34 153.656C204.483 153.656 206.22 151.919 206.22 149.776C206.22 147.633 204.483 145.896 202.34 145.896C200.197 145.896 198.46 147.633 198.46 149.776C198.46 151.919 200.197 153.656 202.34 153.656Z"
          fill="currentColor"
        />
        <path
          d="M179.9 192.506C182.043 192.506 183.78 190.769 183.78 188.626C183.78 186.483 182.043 184.746 179.9 184.746C177.757 184.746 176.02 186.483 176.02 188.626C176.02 190.769 177.757 192.506 179.9 192.506Z"
          fill="currentColor"
        />
        <path
          d="M159.87 155.036C162.013 155.036 163.75 153.299 163.75 151.156C163.75 149.014 162.013 147.276 159.87 147.276C157.727 147.276 155.99 149.014 155.99 151.156C155.99 153.299 157.727 155.036 159.87 155.036Z"
          fill="currentColor"
        />
        <path
          d="M169.73 156.846C165.46 164.066 178.66 164.976 178.19 166.646C177.71 168.316 174.83 167.276 173.88 167.356C172.67 167.466 171.07 169.146 171.39 170.316C172.72 175.076 184.74 180.236 191.12 169.196C197.5 158.146 187.01 150.316 182.23 151.546C181.06 151.846 180.4 154.076 180.91 155.176C181.31 156.036 183.65 158.016 182.45 159.266C181.24 160.526 173.85 149.546 169.73 156.846Z"
          stroke="currentColor"
          strokeWidth={4}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <path
        d="M233.48 137.806C233.48 173.606 214.03 204.866 185.12 221.596"
        stroke="currentColor"
        strokeWidth={4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M136.75 137.806H233.48"
        stroke="currentColor"
        strokeWidth={4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M136.75 137.816L185.11 221.586"
        stroke="currentColor"
        strokeWidth={4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M219.9 148.166C218.56 158.966 215.08 169.686 209.3 179.696C201.67 192.916 190.98 203.266 178.64 210.376"
        stroke="currentColor"
        strokeWidth={4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);
